import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import { getNotifSettings, updateNotifSettings } from '@/lib/api';

const ROW = ({ id, label, hint, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b last:border-b-0">
    <div className="flex-1">
      <label htmlFor={id} className="font-medium text-sm cursor-pointer">{label}</label>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
    <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={`notif-${id}`} />
  </div>
);

export default function NotificationSettings({ userType }) {
  const [settings, setSettings] = useState({ posts_stories: true, chat_new: true, chat_message: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getNotifSettings();
        setSettings(prev => ({ ...prev, ...res.data }));
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = async (key) => {
    const newVal = !settings[key];
    setSettings(prev => ({ ...prev, [key]: newVal }));
    setSaving(true);
    try {
      await updateNotifSettings({ [key]: newVal });
      toast.success('Indstilling opdateret');
    } catch (e) {
      setSettings(prev => ({ ...prev, [key]: !newVal }));
      toast.error(e.response?.data?.detail || 'Kunne ikke opdatere');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="notification-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" /> Notifikationer
        </CardTitle>
        <CardDescription>
          Vælg hvilke notifikationer du vil modtage som popup på skærmen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Indlæser...</div>
        ) : (
          <>
            {userType === 'user' && (
              <ROW
                id="posts_stories"
                label="Opslag og stories fra fulgte virksomheder"
                hint="Få besked når en virksomhed du følger, deler nyt indhold."
                checked={settings.posts_stories}
                onChange={() => toggle('posts_stories')}
                disabled={saving}
              />
            )}
            {userType === 'user' && (
              <ROW
                id="chat_new"
                label="Ny chat oprettet af virksomhed"
                hint="Når en virksomhed starter en chat med dig efter et tilbud."
                checked={settings.chat_new}
                onChange={() => toggle('chat_new')}
                disabled={saving}
              />
            )}
            <ROW
              id="chat_message"
              label="Ny besked i en chat"
              hint="Få besked når modparten skriver i en eksisterende chat."
              checked={settings.chat_message}
              onChange={() => toggle('chat_message')}
              disabled={saving}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
